// Onglet Dashboard "Alphabets" : vitrine des 11 écritures prises en charge
// (remplace l'ancienne page /alphabets, qui pesait ~340 Ko en grande partie
// à cause de données SVG dupliquées). Le Latin est généré en direct avec le
// vrai moteur du site (comme sur la page Proposer) ; les 9 autres écritures
// utilisent une phrase d'exemple FIGÉE (voir alphabetsShowcaseData.js) --
// volontairement pas synchronisée avec la base : c'est une vitrine, pas un
// reflet de l'activité réelle (qui serait vide ou peu flatteuse pour une
// écriture encore rarement utilisée).
//
// Chargé à la demande (premier clic sur l'onglet), même logique que
// sphereSettingsDashboard.js : pas de poids ajouté aux autres pages qui
// partagent le bundle app.js.

const SCRIPT_LABELS = [
  { key: 'Latn', label: 'Latin' },
  { key: 'Arab', label: 'Arabe' },
  { key: 'Deva', label: 'Devanagari' },
  { key: 'Hani', label: 'Chinois' },
  { key: 'Cyrl', label: 'Cyrillique' },
  { key: 'Beng', label: 'Bengali' },
  { key: 'Hebr', label: 'Hébreu' },
  { key: 'Thai', label: 'Thaï' },
  { key: 'Hang', label: 'Coréen' },
  { key: 'Grek', label: 'Grec' },
  { key: 'Jpan', label: 'Japonais' },
];

const LATIN_SENTENCE = 'le MOMENT est venu';

// 10 animations tournent simultanément ici (contrairement à Proposer, une
// seule à la fois) : sans plafond, chacune rasterise à la pleine résolution
// écran (jusqu'à 3x sur mobile), ce qui rendait le défilement saccadé.
// Invisible à la petite taille d'affichage de ces cartes.
const CARD_MAX_DPR = 1.5;

// Légende affichée sous chaque nom d'écriture : le mot mis en évidence par
// le knockout doit rester repérable même sans le voir animé. Marqueur *mot*
// unifié sur les 9 écritures non-latines, comme partout ailleurs sur le site
// (Proposer, /alphabets, cf. commit d42137b) -- le Latin, lui, utilise
// directement une MAJUSCULE (rendu en direct, pas ce marqueur). Texte figé
// (repris tel quel de l'ancienne page /alphabets), indépendant de lineData
// (qui ne sert qu'au rendu animé).
const DISPLAY_SENTENCES = {
  Latn: LATIN_SENTENCE,
  Arab: 'لقد حان *الوقت*',
  Deva: '*समय* आ गया है',
  Hani: '*时机*已到',
  Cyrl: '*ВРЕМЯ* пришло',
  Beng: '*সময়* এসেছে',
  Hebr: 'הגיע *הזמן*',
  Thai: '*เวลา*ได้มาถึงแล้ว',
  Hang: '*때*가 왔다',
  Grek: 'ήρθε η *ΩΡΑ*',
  Jpan: '*時*が来た',
};

export function createAlphabetsDashboardFeature() {
  const grid = document.getElementById('dash-alphabets-grid');

  let loaded = false;

  async function load() {
    if (loaded) return;
    loaded = true;

    const [
      { parseFont },
      { prepareAnimLine, drawLine },
      { createPunchlineAnimationFeature },
      { ALPHABET_SHOWCASE_ENTRIES },
    ] = await Promise.all([
      import('../../gen_svg.js'),
      import('../animation/lineEngine.js'),
      import('../animation/punchlineAnimation.js'),
      import('./alphabetsShowcaseData.js'),
    ]);

    const resizers = [];
    window.addEventListener('resize', () => resizers.forEach((r) => r()));

    // Chaque écriture a son propre ratio hauteur/largeur (canvasH/pw) --
    // sans forcer un ratio commun, chaque carte prend une hauteur différente
    // (grille inégale) et, comme le fond du canvas (blanc, cf. bg dans
    // punchlineAnimation.js) ne couvre que sa propre hauteur, l'écart avec
    // le fond noir du conteneur laisse une marge visible sur certaines
    // cartes seulement. En imposant à toutes le même ratio (le plus grand
    // des 9 écritures figées), toutes les cartes ont la même hauteur ; le
    // fond du conteneur est mis au blanc (cf. CSS) pour que tout espace
    // résiduel de recentrage se fonde dans le canvas.
    const sharedAspectRatio = Math.max(
      ...ALPHABET_SHOWCASE_ENTRIES.map((e) => e.lineData.canvasH / e.lineData.pw)
    );

    grid.innerHTML = '';
    for (const { key, label } of SCRIPT_LABELS) {
      const card = document.createElement('div');
      card.className = 'dash-alphabet-card';
      const sentence = DISPLAY_SENTENCES[key] ?? '';
      card.innerHTML = `
        <p class="dash-alphabet-card-head"><b>${label}</b><span class="dash-alphabet-sentence">${sentence}</span></p>
        <div class="dash-alphabet-stage"></div>
      `;
      grid.appendChild(card);
      const stage = card.querySelector('.dash-alphabet-stage');

      if (key === 'Latn') {
        let fontBuffer = null;
        const loadFont = async () => {
          if (fontBuffer) return fontBuffer;
          const res = await fetch('/assets/fonts/nocode.otf');
          fontBuffer = await res.arrayBuffer();
          return fontBuffer;
        };
        let animFont = null;
        const anim = createPunchlineAnimationFeature({
          punchlineAnimPreview: stage,
          drawLine, loadFont, parseFont, prepareAnimLine,
          getAnimFont: () => animFont,
          setAnimFont: (f) => { animFont = f; },
          maxDpr: CARD_MAX_DPR,
        });
        anim.setAspectRatio(sharedAspectRatio);
        anim.render(LATIN_SENTENCE);
        resizers.push(() => anim.resize());
        continue;
      }

      // Écritures non-latines : données figées (aucun appel au pipeline
      // HarfBuzz/Potrace, trop lent pour un affichage à la demande -- voir
      // alphabetsShowcaseData.js). getAnimFont/parseFont/loadFont ne servent
      // pas ici (renderFromData n'en a pas besoin), gardés à null pour
      // matcher la signature attendue par createPunchlineAnimationFeature.
      const entry = ALPHABET_SHOWCASE_ENTRIES.find((e) => e.script === key);
      if (!entry) continue;
      const anim = createPunchlineAnimationFeature({
        punchlineAnimPreview: stage,
        drawLine,
        loadFont: async () => null,
        parseFont: async () => null,
        prepareAnimLine: async () => null,
        getAnimFont: () => null,
        setAnimFont: () => {},
        maxDpr: CARD_MAX_DPR,
      });
      anim.setAspectRatio(sharedAspectRatio);
      anim.renderFromData(entry.lineData);
      resizers.push(() => anim.resize());
    }
  }

  return { load };
}
